import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { logger } from './logger.js';
import { platformBrandsRepo } from './repositories/platform-brands-repo.js';
import { loadBrand, resolveBrandIdForHost } from './brand-resolver.js';
import type { PlatformBrand } from '@kilat/shared';
import { CONNECTION_GATE_TIMEOUT_MS, ConnectionGateError } from '@kilat/ui';

const BrandContext = createContext<PlatformBrand | null>(null);

export interface PlatformBrandProviderProps {
  readonly host?: string; // override for tests; defaults to window.location.hostname
  readonly children: ReactNode;
  readonly fallback?: ReactNode; // shown until first paint with brand
}

/**
 * Resolves the platform brand at boot and provides it via context.
 * Subscribes to live brand updates so a super-admin save propagates instantly.
 */
export function PlatformBrandProvider({
  host,
  children,
  fallback = null,
}: PlatformBrandProviderProps) {
  const [brand, setBrand] = useState<PlatformBrand | null>(null);
  const [failed, setFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let unsubLive: (() => void) | null = null;
    setFailed(false);
    // Nothing renders until this read lands, and an offline Firestore read
    // waits rather than failing — so give it a deadline, then say so.
    const deadline = window.setTimeout(() => {
      if (!cancelled) setFailed(true);
    }, CONNECTION_GATE_TIMEOUT_MS);

    (async () => {
      try {
        const resolvedHost = host ?? window.location.hostname;
        const brandId = await resolveBrandIdForHost(resolvedHost);
        const initial = await loadBrand(brandId);
        if (cancelled) return;
        window.clearTimeout(deadline);
        setBrand(initial);
        unsubLive = platformBrandsRepo.snapshot(brandId, (next) => {
          if (cancelled || !next) return;
          setBrand(next);
        });
      } catch (e) {
        logger.error('PlatformBrandProvider: failed to resolve brand', { error: String(e) });
        // Used to stop here: the app sat on its splash forever.
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(deadline);
      if (unsubLive) unsubLive();
    };
  }, [host, retryTick]);

  const value = useMemo(() => brand, [brand]);
  if (!brand && failed) {
    return <ConnectionGateError onRetry={() => setRetryTick((n) => n + 1)} />;
  }
  if (!value) return <>{fallback}</>;
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function usePlatformBrand(): PlatformBrand {
  const v = useContext(BrandContext);
  if (!v) {
    throw new Error('usePlatformBrand must be used inside <PlatformBrandProvider>');
  }
  return v;
}
